/**
 * VariableSpending — spending categories with per-category budgets and entry logging.
 * Statement import is now in the dedicated Import tab (Money → Import).
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  fc,
  fdate,
  catTotal,
  getFrequentPlaces,
  VCATS,
  mk,
} from '../../utils';

export function VariableSpending({
  cats,
  activeVd,
  catBudgets,
  varSpend,
  k,
  activeInc,
  activeVarBudget,
  activeVarTot,
  activeVarRem,

  anyCatOver,
  varRem,
  budgetSlider,
  customCats,
  disabledCats,
  state = {},
  updateState,
  showToast,
}) {
  const [varOpen, setVarOpen] = useState({});
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatEmoji, setNewCatEmoji] = useState('🏷️');
  const [newCatCol, setNewCatCol] = useState('#4d96ff');
  const [undoItem, setUndoItem] = useState(null);       // { catId, moKey, item, label }
  const [pendingDup, setPendingDup] = useState(null);  // { catId, place, amt } — awaiting confirmation
  const undoTimerRef = useRef(null);

  // Clean up the undo timer if the component unmounts mid-countdown
  useEffect(() => () => { if (undoTimerRef.current) clearTimeout(undoTimerRef.current); }, []);
  const varPlaceRefs = useRef({});
  const varAmtRefs = useRef({});

  const handleAddCat = () => {
    if (!newCatName.trim()) { showToast('Enter a name'); return; }
    const id = 'cat_' + Date.now();
    const next = [...(customCats || []), { id, name: newCatName.trim(), em: newCatEmoji, col: newCatCol }];
    updateState({ customCats: next });
    setNewCatName(''); setAddCatOpen(false);
    showToast('Category added!');
  };

  const handleRemoveCat = (id) => {
    const hasSpend = Object.values(varSpend || {}).some((mo) => (mo[id] || []).length > 0);
    if (hasSpend && !window.confirm('This category has spending logged. Remove it anyway? Existing entries will move to Other.')) return;
    const updatedVarSpend = {};
    Object.entries(varSpend || {}).forEach(([mo, cats]) => {
      const moData = { ...cats };
      if (moData[id] && moData[id].length > 0) {
        moData.oth = [...(moData.oth || []), ...moData[id]];
        delete moData[id];
      }
      updatedVarSpend[mo] = moData;
    });
    updateState({ customCats: (customCats || []).filter((c) => c.id !== id), varSpend: updatedVarSpend });
  };

  const handleToggleBuiltin = (id) => {
    const cur = disabledCats || [];
    updateState({ disabledCats: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  // Bank auto-adjust helpers — only fire for current month and when balance is set
  const _todayStr = new Date().toISOString().slice(0, 10);
  const _curMoKey = mk(new Date().getFullYear(), new Date().getMonth());
  const bankAdjust = (delta, moKey) =>
    state.bankBalance > 0 && moKey === _curMoKey
      ? { bankBalance: Math.max(0, Math.round((state.bankBalance + delta) * 100) / 100), bankBalanceDate: _todayStr }
      : {};

  const commitAddVar = (catId, place, amt) => {
    const updated = { ...varSpend };
    if (!updated[k]) updated[k] = {};
    if (!updated[k][catId]) updated[k][catId] = [];
    const newItems = [...(updated[k][catId] || []), { place, amt, ts: Date.now(), date: new Date().toISOString().slice(0, 10) }];
    updated[k] = { ...updated[k], [catId]: newItems };
    updateState({ varSpend: updated, ...bankAdjust(-amt, k) });
    if (varPlaceRefs.current[catId]) varPlaceRefs.current[catId].value = '';
    if (varAmtRefs.current[catId]) varAmtRefs.current[catId].value = '';
    const catBud = (catBudgets || {})[catId] || 0;
    if (catBud > 0) {
      const newTotal = catTotal(newItems);
      if (newTotal > catBud) showToast(`⚠️ Over ${cats.find((c) => c.id === catId)?.name || catId} budget by ${fc(newTotal - catBud)}`);
      else showToast('Added!');
    } else {
      showToast('Added!');
    }
  };

  const handleAddVar = (catId) => {
    const place = varPlaceRefs.current[catId]?.value?.trim();
    const amt = parseFloat(varAmtRefs.current[catId]?.value) || 0;
    if (!place) { showToast('Enter where you spent it'); return; }
    if (!amt || amt <= 0) { showToast('Enter an amount'); return; }
    const existing = (varSpend[k] || {})[catId] || [];
    const now = Date.now();
    // Duplicate guard: same place + amount within 5 minutes — block and ask for confirmation
    const isDup = existing.some(
      (x) =>
        x.place.toLowerCase() === place.toLowerCase() &&
        Math.abs(x.amt - amt) < 0.01 &&
        now - x.ts < 5 * 60 * 1000
    );
    if (isDup) {
      setPendingDup({ catId, place, amt });
      return; // do NOT save yet — wait for confirmation
    }
    commitAddVar(catId, place, amt);
  };

  // User confirmed they really want to add the duplicate
  const confirmDup = () => {
    if (!pendingDup) return;
    const { catId, place, amt } = pendingDup;
    setPendingDup(null);
    commitAddVar(catId, place, amt);
  };

  const handleDelVar = (catId, item) => {
    // item carries _moKey and _origIdx so deletes work across month buckets
    const moKey = item._moKey ?? k;
    const origIdx = item._origIdx ?? 0;
    const updated = { ...varSpend };
    updated[moKey] = {
      ...updated[moKey],
      [catId]: (updated[moKey][catId] || []).filter((_, i) => i !== origIdx),
    };
    updateState({ varSpend: updated, ...bankAdjust(item.amt, moKey) }); // add back what was deducted
    // Surface an undo banner for 5 seconds
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoItem({ catId, moKey, item, label: item.place });
    undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
  };

  const handleUndoDelete = () => {
    if (!undoItem) return;
    clearTimeout(undoTimerRef.current);
    setUndoItem(null);
    const { catId, moKey, item } = undoItem;
    const pure = { place: item.place, amt: item.amt, ts: item.ts };
    const updated = { ...varSpend };
    if (!updated[moKey]) updated[moKey] = {};
    const existing = [...(updated[moKey][catId] || [])];
    const insertAt = Math.min(item._origIdx ?? existing.length, existing.length);
    existing.splice(insertAt, 0, pure);
    updated[moKey] = { ...updated[moKey], [catId]: existing };
    updateState({ varSpend: updated, ...bankAdjust(-item.amt, moKey) }); // re-deduct on restore
    showToast('Restored!');
  };

  const dispPct = activeVarBudget > 0 ? Math.min(100, (activeVarTot / activeVarBudget) * 100).toFixed(1) : 0;
  const remColor = activeVarRem < 0 ? 'var(--red)' : anyCatOver ? 'var(--ylw)' : 'var(--grn)';

  return (
    <div className="card cp">
      {/* Duplicate confirmation banner */}
      {pendingDup && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', marginBottom: 10, background: 'rgba(255,217,61,.1)', border: '1px solid rgba(255,217,61,.4)', borderRadius: 8, fontSize: 12 }}>
          <span style={{ color: 'var(--txt)' }}>⚠️ <strong>{pendingDup.place}</strong> for {fc(pendingDup.amt)} was just logged — add again?</span>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8 }}>
            <button onClick={confirmDup} style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 7, border: '1.5px solid var(--ylw)', background: 'rgba(255,217,61,.15)', color: 'var(--ylw)', cursor: 'pointer' }}>
              Yes, add
            </button>
            <button onClick={() => setPendingDup(null)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 7, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Undo banner — appears for 5 s after a delete */}
      {undoItem && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', marginBottom: 10, background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.3)', borderRadius: 8, fontSize: 12 }}>
          <span style={{ color: 'var(--mut)' }}>Deleted <strong>{undoItem.label}</strong></span>
          <button onClick={handleUndoDelete} style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 7, border: '1.5px solid var(--ylw)', background: 'rgba(255,217,61,.12)', color: 'var(--ylw)', cursor: 'pointer' }}>
            Undo
          </button>
        </div>
      )}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="st" style={{ marginBottom: 0 }}>✏️ Variable Spending</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: 'var(--mut)' }}>Budget:</span>
          <input type="range" min="0" max="100" step="5" value={budgetSlider ?? 50} onChange={(e) => updateState({ budgetSlider: +e.target.value })} style={{ width: 70, accentColor: 'var(--ylw)', touchAction: 'none' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ylw)', minWidth: 28 }}>{budgetSlider ?? 50}%</span>
        </div>
      </div>

      {/* Budget bar */}
      {activeInc > 0 && (budgetSlider ?? 50) > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--mut)' }}>Monthly Budget</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: remColor }}>
              {activeVarRem < 0 ? '−' + fc(Math.abs(activeVarRem)) + ' over' : fc(activeVarRem) + ' left'}
            </span>
          </div>
          <div className="pw"><div className="pb" style={{ background: remColor, width: dispPct + '%' }} /></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--mut)', marginTop: 2 }}>
            <span>{fc(activeVarTot)} spent</span><span>{fc(activeVarBudget)} budget</span>
          </div>
          {anyCatOver && varRem >= 0 && (
            <div style={{ marginTop: 6, padding: '5px 10px', borderRadius: 7, background: 'rgba(255,217,61,.08)', border: '1px solid rgba(255,217,61,.25)', fontSize: 11, color: 'var(--ylw)', fontWeight: 600 }}>
              ⚠️ A spending category is over its limit
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 10 }}>Tap to expand &amp; log expenses</div>

      {/* Category manager */}
      <div style={{ marginBottom: 10 }}>
        <button onClick={() => setCatMgrOpen((o) => !o)} style={{ fontSize: 11, color: 'var(--mut)', background: 'none', border: '1px solid var(--bdr)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
          ⚙️ Manage categories {catMgrOpen ? '▾' : '▸'}
        </button>
        {catMgrOpen && (
          <div style={{ marginTop: 8, padding: '10px', background: 'var(--surf2)', borderRadius: 8, border: '1px solid var(--bdr)' }}>
            <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px' }}>Built-in</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
              {VCATS.map((c) => {
                const off = (disabledCats || []).includes(c.id);
                return (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13 }}>{c.em}</span>
                    <span style={{ flex: 1, fontSize: 12, color: off ? 'var(--mut)' : c.col, fontWeight: 600, textDecoration: off ? 'line-through' : '' }}>{c.name}</span>
                    <button onClick={() => handleToggleBuiltin(c.id)} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, border: `1px solid ${off ? 'var(--bdr)' : c.col}`, background: 'none', color: off ? 'var(--mut)' : c.col, cursor: 'pointer' }}>
                      {off ? 'Enable' : '✓ On'}
                    </button>
                  </div>
                );
              })}
            </div>
            {(customCats || []).length > 0 && (
              <>
                <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px' }}>Custom</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                  {(customCats || []).map((c) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13 }}>{c.em}</span>
                      <span style={{ flex: 1, fontSize: 12, color: c.col, fontWeight: 600 }}>{c.name}</span>
                      <button onClick={() => handleRemoveCat(c.id)} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}>Remove</button>
                    </div>
                  ))}
                </div>
              </>
            )}
            {addCatOpen ? (
              <div style={{ borderTop: '1px solid var(--bdr)', paddingTop: 8 }}>
                <input className="ob-inp" placeholder="Category name" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} style={{ marginBottom: 6, width: '100%' }} />
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 6 }}>
                  {['🏷️', '⭐', '🐾', '💊', '✈️', '🎓', '🔧', '🏠', '🎸', '🎯'].map((em) => (
                    <button key={em} onClick={() => setNewCatEmoji(em)} style={{ fontSize: 16, padding: '3px 5px', borderRadius: 6, border: `1.5px solid ${newCatEmoji === em ? 'var(--ylw)' : 'var(--bdr)'}`, background: 'none', cursor: 'pointer' }}>{em}</button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 5, marginBottom: 8, flexWrap: 'wrap' }}>
                  {['#ff6b6b', '#4d96ff', '#ffd93d', '#6bcb77', '#ff6bdf', '#7C6FF7', '#f0a500'].map((c) => (
                    <button key={c} onClick={() => setNewCatCol(c)} style={{ width: 20, height: 20, borderRadius: '50%', background: c, border: `2px solid ${newCatCol === c ? '#fff' : 'transparent'}`, cursor: 'pointer' }} />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="bsave" onClick={handleAddCat}>Add</button>
                  <button className="bcancel" onClick={() => setAddCatOpen(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddCatOpen(true)} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 7, border: '1px dashed var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer', width: '100%' }}>+ Add custom category</button>
            )}
          </div>
        )}
      </div>

      {/* Category rows */}
      {cats.map((cat) => {
        const allItems = activeVd[cat.id] || [];
        const tot = catTotal(allItems);
        const open = !!varOpen[cat.id];
        const catBud = (catBudgets || {})[cat.id] || 0;
        const catRem = catBud - tot;
        const catPct = catBud > 0 ? Math.min(100, (tot / catBud) * 100).toFixed(1) : 0;
        const catOv = catBud > 0 && tot > catBud;

        return (
          <div key={cat.id} style={{ borderBottom: '1px solid var(--bdr)' }}>
            <div className="vr" onClick={() => setVarOpen((o) => ({ ...o, [cat.id]: !o[cat.id] }))}>
              <div className="ve">{cat.em}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="vl" style={{ marginBottom: tot > 0 ? 3 : 0 }}>{cat.name}</div>
                {tot > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ flex: 1, background: 'var(--bdr)', borderRadius: 99, height: 4, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 99, transition: 'width .4s',
                        background: catOv ? 'var(--red)' : cat.col,
                        width: catBud > 0
                          ? catPct + '%'
                          : activeVarTot > 0 ? Math.min(100, (tot / activeVarTot) * 100).toFixed(1) + '%' : '0%',
                      }} />
                    </div>
                    <span style={{ fontSize: 10, whiteSpace: 'nowrap', fontWeight: 600,
                      color: catBud > 0
                        ? (catOv ? 'var(--red)' : catRem < catBud * 0.15 ? 'var(--ylw)' : 'var(--mut)')
                        : 'var(--mut)',
                    }}>
                      {catBud > 0
                        ? (catOv ? '−' + fc(Math.abs(catRem)) + ' over' : fc(catRem) + ' left')
                        : activeVarTot > 0 ? Math.round((tot / activeVarTot) * 100) + '% of total' : ''}
                    </span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {tot > 0 ? (
                  <span className="vt" style={{ color: catOv ? 'var(--red)' : cat.col }}>
                    {fc(tot)}{catBud > 0 && <span style={{ fontSize: 10, color: 'var(--mut)', fontWeight: 400 }}> / {fc(catBud)}</span>}
                  </span>
                ) : (
                  <span className="va">{catBud > 0 ? '$0 / ' + fc(catBud) : '$0'}</span>
                )}
                <span style={{ fontSize: 11, color: 'var(--mut)' }}>{open ? '▾' : '▸'}</span>
              </div>
            </div>

            {open && (
              <div className="vexp">
                {(() => {
                  const freq = getFrequentPlaces(varSpend, cat.id);
                  return freq.length > 0 && (
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                      {freq.map((place) => (
                        <button key={place} onClick={() => { if (varPlaceRefs.current[cat.id]) varPlaceRefs.current[cat.id].value = place; }} style={{ fontSize: 10, padding: '3px 9px', background: 'var(--surf2)', border: '1px solid var(--bdr)', borderRadius: 99, color: 'var(--mut)', cursor: 'pointer', fontFamily: 'inherit' }}>
                          {place}
                        </button>
                      ))}
                    </div>
                  );
                })()}
                <div className="vaf">
                  <input type="text" placeholder="Where?" ref={(el) => (varPlaceRefs.current[cat.id] = el)} onKeyDown={(e) => e.key === 'Enter' && handleAddVar(cat.id)} />
                  <div className="vaw">
                    <span>$</span>
                    <input type="number" min="0" step="0.01" placeholder="0.00" ref={(el) => (varAmtRefs.current[cat.id] = el)} onKeyDown={(e) => e.key === 'Enter' && handleAddVar(cat.id)} />
                  </div>
                  <button className="vad" onClick={() => handleAddVar(cat.id)}>+</button>
                </div>
                {!allItems.length ? (
                  <div className="em">No entries yet</div>
                ) : (
                  [...allItems].sort((a, b) => b.ts - a.ts).map((it, si) => {
                    const d = new Date(it.ts);
                    return (
                      <div key={si} className="vit">
                        <div className="vip">
                          <div className="vin">{it.place}</div>
                          <div className="vid">{fdate(d)} · {d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
                        </div>
                        <div className="vic" style={{ color: cat.col }}>{fc(it.amt)}</div>
                        <button className="del" title="Re-log" style={{ color: 'var(--blu)', fontSize: 13 }} onClick={() => { commitAddVar(cat.id, it.place, it.amt); showToast?.('↩ Re-logged: ' + it.place); }}>↩</button>
                        <button className="del" onClick={() => handleDelVar(cat.id, it)}>✕</button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
}
